'use client';
import React, { useState, useEffect } from 'react';
import { Shield, Star, Users, Clock, Verified, Share2, Bookmark, AlertTriangle } from 'lucide-react';

interface ActivityHeroProps {
  onJoin: () => void;
}

export default function ActivityHero({ onJoin }: ActivityHeroProps) {
  const [timeLeft, setTimeLeft] = useState({ hours: 4, minutes: 22, seconds: 18 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatNum = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      {/* Banner */}
      <div className="relative h-40 md:h-56 flex items-center justify-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #0D1424 40%, #0a1428 100%)' }}>
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-25 blur-3xl"
            style={{ background: 'radial-gradient(circle, #7C3AED, transparent)' }} />
        </div>
        {/* Category badge */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="badge-paid text-xs px-3 py-1.5 rounded-full font-bold">PAID COMPETITION</span>
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold badge-live">
            <span className="status-dot-live" />
            LIVE
          </span>
        </div>
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button className="p-2 rounded-lg transition-colors hover:bg-muted/50" style={{ background: 'rgba(0,0,0,0.4)' }} aria-label="Share">
            <Share2 size={16} style={{ color: '#94A3B8' }} />
          </button>
          <button className="p-2 rounded-lg transition-colors hover:bg-muted/50" style={{ background: 'rgba(0,0,0,0.4)' }} aria-label="Bookmark">
            <Bookmark size={16} style={{ color: '#94A3B8' }} />
          </button>
        </div>

        {/* Emoji + title */}
        <div className="text-center relative z-10 px-4">
          <div className="text-5xl mb-3">🏆</div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#A78BFA' }}>National Trivia Championship</p>
        </div>
      </div>

      {/* Info bar */}
      <div className="p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-xl md:text-2xl font-extrabold text-foreground leading-tight">
                National Trivia Championship — Q3 2026
              </h1>
              <div className="flex items-center gap-1 flex-shrink-0" title="Verified organizer">
                <Shield size={16} style={{ color: '#06B6D4' }} />
              </div>
            </div>
            <p className="text-sm mb-3" style={{ color: 'var(--muted-foreground)' }}>
              Organized by <span className="font-semibold" style={{ color: '#A78BFA' }}>Brain Basket Official</span>
              <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(6,182,212,0.15)', color: '#06B6D4' }}>
                <Shield size={10} />
                Verified
              </span>
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: 'var(--muted-foreground)' }}>
              <span className="flex items-center gap-1.5"><Users size={14} />2,847 / 5,000 participants</span>
              <span className="flex items-center gap-1.5"><Star size={14} style={{ color: '#F59E0B' }} />4.9 (1,204 ratings)</span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} />
                <span style={{ color: '#10B981' }}>Open for entries</span>
              </span>
            </div>
          </div>

          {/* Countdown timer */}
          <div className="flex-shrink-0 text-center p-4 rounded-xl"
            style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#A78BFA' }}>
              <Clock size={10} className="inline mr-1" />
              Starts In
            </p>
            <div className="flex items-center gap-1">
              {[
                { val: formatNum(timeLeft.hours), label: 'h' },
                { val: formatNum(timeLeft.minutes), label: 'm' },
                { val: formatNum(timeLeft.seconds), label: 's' },
              ].map((unit, i) => (
                <React.Fragment key={`timer-${unit.label}`}>
                  {i > 0 && <span className="text-lg font-bold" style={{ color: '#A78BFA' }}>:</span>}
                  <div className="text-center">
                    <div className="text-2xl font-extrabold" style={{ color: 'var(--foreground)', fontVariantNumeric: 'tabular-nums', minWidth: '36px' }}>
                      {unit.val}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)', fontSize: '10px' }}>{unit.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Compliance notice */}
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg"
          style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <AlertTriangle size={14} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '1px' }} />
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <span className="font-semibold" style={{ color: '#F59E0B' }}>Paid Competition Notice:</span> Entry fee of $10 is required to participate. Funds are held in escrow until results are validated. Platform fee: 10%. Winners receive prizes within 24 hours of result validation. Must be 18+. Available in eligible regions only.
          </p>
        </div>
      </div>
    </div>
  );
}